import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { Form, FormInstance, Input, Modal, Radio, Table, Flex } from 'antd'
import {
    CheckCircleFilled,
    EyeInvisibleOutlined,
    EyeOutlined,
    FlagOutlined,
    FolderOutlined,
    Loading3QuartersOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { AppService } from '@halo/services/app.service'
import { FlagModel } from '@halo/models/flag.model'
import { Button, FilterBar, PageHeader, StatusBadge } from '@pushly/aqe/lib/components'
import type {
    ActiveFilter,
    ButtonAction,
    FilterDefinition,
    FilterSelectOption,
} from '@pushly/aqe/lib/components'
import EntityDisplay, { EntityTitle } from '@halo/components/data-table-view/entity-display'
import './style/flags-list.scss'
import { StatusName } from '@pushly/aquarium-utils/lib/enums'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { FlagsEditor } from '@halo/portals/post-auth/flags/flag-data-form'
import randomstring from 'randomstring'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { useLoadableDataState, useService } from '@pushly/aqe/lib/hooks/index'
import { TablePaginationConfig } from 'antd/es/table/interface'
import titleCase from 'title-case'
import { simpleNotification } from '@halo/_utils/notifications'
import deepEqual from 'react-fast-compare'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'

interface IFlagsListProps {}

interface IFlagEditorState {
    drawerKey?: string
    showFlagEditor: boolean
    flagToEdit: FlagModel | Partial<FlagModel>
}

interface IFlagsDataState {
    flags: FlagModel[]
    meta: any
}

export const FlagsList = (props: IFlagsListProps) => {
    const appSvc = useService(AppService)
    const formRef = React.createRef<FormInstance>()

    // ---------------------------------- Stateful Variables ---------------------------------- //
    const [flags, setFlags] = useLoadableDataState<IFlagsDataState>({ data: { flags: [], meta: {} } })
    const [pagination, setPagination] = React.useState<TablePaginationConfig>({
        current: 1,
        itemRender: (current: any, type: String, originalElement: any) => {
            switch (type) {
                case 'prev':
                    return <a>Previous</a>
                case 'next':
                    return <a>Next</a>
                case 'page':
                    return ''
            }

            return originalElement
        },
        onChange: async (page: number) => {
            await setPagination((config) => ({ ...config, current: page }))
        },
        pageSize: 15,
        showSizeChanger: false,
        showTotal: (total: number, range: number[]) => `Showing ${range[0]}-${range[1]} of ${total} Flags`,
        size: 'small',
        total: 15,
    })
    const [query, setQuery] = React.useState<string | undefined>()
    const [entityFilter, setEntityFilter] = React.useState<string[]>([])
    const [flagsEditor, setFlagsEditor] = React.useState<IFlagEditorState>({
        drawerKey: undefined,
        showFlagEditor: false,
        flagToEdit: new FlagModel(),
    })

    const getFlags = async () => {
        setFlags((state) => ({ ...state, loading: true }))

        const opts: IApiCallOptions = {
            query: {
                limit: pagination.pageSize,
                page: pagination.current,
                search: query,
                entities: entityFilter?.join(','),
            },
        }

        const response = await appSvc.fetchFlags(opts)

        setFlags({ loading: false, data: { flags: response.data, meta: response.meta } })
    }

    React.useEffect(() => {
        if (query?.length > 2 || query === undefined) {
            getFlags()
        }
    }, [pagination.current, query, entityFilter])

    const flagsDataSource = flags.data.flags

    const searchFlags = async (data: string | any): Promise<void> => {
        const value = typeof data !== 'string' ? data.target.value : data
        let qString = (value ?? '').trim()

        if (qString.length < 2) {
            qString = undefined
        }

        setQuery(qString)
    }

    const filterFlags = async (flagFilter: string[]) => {
        const next = flagFilter ?? []
        if (!deepEqual(next, entityFilter)) {
            setEntityFilter(next)
        }
    }

    const filterDefinitions = React.useMemo<FilterDefinition[]>(
        () => [
            {
                key: 'entities',
                label: 'Entities',
                type: 'multi-select',
                showOperatorSelect: false,
                loadOptions: async (): Promise<FilterSelectOption[]> => {
                    try {
                        const filterOpts = await appSvc.getFlagFilters()
                        if (!Array.isArray(filterOpts) || filterOpts.length === 0) return []
                        return filterOpts.map((entity: string) => ({
                            group: 'Entities',
                            label: titleCase(entity),
                            value: entity,
                        }))
                    } catch (error) {
                        simpleNotification('error', error)
                        return []
                    }
                },
            },
        ],
        [],
    )

    const getFilterBarValue = (): ActiveFilter[] => {
        const active: ActiveFilter[] = []
        if (entityFilter.length > 0) {
            active.push({ key: 'entities', operator: 'in', value: entityFilter })
        }
        return active
    }

    const handleFilterBarApply = (filters: ActiveFilter[]) => {
        const nextEntities = (filters.find((f) => f.key === 'entities')?.value as string[]) ?? []
        filterFlags(nextEntities)
    }

    // ---------------------------------- Action Functions ---------------------------------- //
    const renderActions = (flag: FlagModel) => {
        const actions: ButtonAction[] = [
            {
                key: 'archive',
                text: 'Archive',
                icon: <FolderOutlined />,
                onClick: () => handleRowAction('archive', flag, null as any),
            },
        ]

        if (flag.getIsActive()) {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeInvisibleOutlined />,
                onClick: () => handleRowAction('disable', flag, null as any),
            })
        } else {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
                onClick: () => handleRowAction('enable', flag, null as any),
            })
        }

        return (
            <Button
                size="small"
                onClick={(ev) => handlePrimaryClick(flag, ev as React.MouseEvent<HTMLElement>)}
                actions={actions}
            >
                Edit
            </Button>
        )
    }

    const renderFlagDetails = (flag: FlagModel): React.ReactNode => {
        return (
            <EntityDisplay
                top={{
                    left: <EntityTitle>{flag.getKey()}</EntityTitle>,
                    right: (
                        <StatusBadge
                            type="generic"
                            status={flag.getIsActive() ? StatusName.ACTIVE : StatusName.DISABLED}
                            expanded={true}
                        />
                    ),
                }}
                bottom={{
                    left: <span>{flag.getDescription()}</span>,
                }}
            />
        )
    }

    const handleArchiveFlag = async (flag: FlagModel) => {
        await appSvc.archiveFlag(flag.getKey())
        return getFlags()
    }

    const handlePrimaryClick = (flag: FlagModel, _ev: React.MouseEvent<HTMLElement>) => {
        handleOpenFlagDrawer(flag)
    }

    const renderIsActive = (flag: FlagModel, classname: string) => {
        return flag.getIsDevOnly() ? (
            <CheckCircleFilled className={classname} />
        ) : (
            <Radio style={{ pointerEvents: 'none' }} checked={false} />
        )
    }

    const handleFlagActiveStatus = async (flag: FlagModel, updateToStatus: boolean) => {
        flag.setIsActive(updateToStatus)
        await appSvc.updateFlag(flag, flag.getKey())
        return getFlags()
    }

    const handleRowAction = (
        key: string | number,
        flag: FlagModel,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'archive') {
            Modal.confirm({
                title: 'Archive Flag',
                icon: <WarningOutlined />,
                okText: 'Archive',
                onOk: async () => {
                    if (formRef.current) {
                        await formRef.current.validateFields()
                        return handleArchiveFlag(flag)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to archive this flag? The flag will no longer be available for use.</p>

                        <p>
                            Please type the Flag KEY, <b>{flag.getKey()}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule, value) => {
                                        if (!value) {
                                            return Promise.reject('Flag KEY is required.')
                                        } else if (value?.trim() !== flag.getKey()) {
                                            return Promise.reject('Flag KEY does not match.')
                                        } else {
                                            return Promise.resolve()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'disable') {
            Modal.confirm({
                title: 'Disable Flag',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    if (formRef.current) {
                        await formRef.current.validateFields()
                        return handleFlagActiveStatus(flag, false)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to disable this flag?</p>

                        <p>
                            Please type the Flag KEY, <b>{flag.getKey()}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule, value, callback) => {
                                        if (!value) {
                                            return Promise.reject('Flag name is required.')
                                        } else if (value?.trim() !== flag.getKey()) {
                                            return Promise.reject('Flag name does not match.')
                                        } else {
                                            return Promise.resolve()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else {
            Modal.confirm({
                title: 'Enable Flag',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => handleFlagActiveStatus(flag, true),
                content: (
                    <p>
                        Are you sure you want to enable this flag? The flag effect will immediately be active on all
                        domains which have this flag.
                    </p>
                ),
            } as any)
        }
    }

    const handleOpenFlagDrawer = async (flag?: FlagModel) => {
        const flagToEdit = flag ?? new FlagModel()
        return setFlagsEditor({
            drawerKey: randomstring.generate(),
            flagToEdit,
            showFlagEditor: true,
        })
    }

    const handleCloseFlagDrawer = async () => {
        return setFlagsEditor({
            showFlagEditor: false,
            flagToEdit: new FlagModel(),
        })
    }

    const onFlagUpdated = () => {
        handleCloseFlagDrawer()
        getFlags()
    }

    const tableHeader = (
        <Flex className="flags-list-header" gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center">
                <Input.Search
                    className="flags-list-search"
                    style={{ width: 300 }}
                    size="middle"
                    placeholder="Search flag name ..."
                    enterButton={true}
                    onSearch={searchFlags}
                />
                <FilterBar
                    filterDefinitions={filterDefinitions}
                    value={getFilterBarValue()}
                    onApply={handleFilterBarApply}
                    showInlineTags={false}
                    showInlineClearAll={false}
                    size="middle"
                />
            </Flex>
        </Flex>
    )

    function render() {
        return (
            <div className={classnames('flags-list')}>
                <PageHeader
                    title="Flags"
                    onTitleSet={noop}
                    action={
                        <Button type="primary" onClick={() => handleOpenFlagDrawer()}>
                            New Flag
                        </Button>
                    }
                />

                <Table
                    className="aqe-table-well"
                    title={() => tableHeader}
                    bordered={false}
                    dataSource={flagsDataSource}
                    loading={
                        flags.loading && {
                            indicator: <Loading3QuartersOutlined spin={true} />,
                        }
                    }
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Flags" icon={<FlagOutlined />} />,
                    }}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={flags.data.meta?.links}
                            onPrev={(_ev, _link, config) => {
                                setPagination({
                                    ...pagination,
                                    current: config.current!,
                                })
                            }}
                            onNext={(_ev, _link, config) => {
                                setPagination({
                                    ...pagination,
                                    current: config.current!,
                                })
                            }}
                        />
                    )}
                    rowKey={(flag) => flag.getKey()}
                    size="small"
                >
                    <Table.Column
                        align="left"
                        className="flag-details"
                        title="Flag"
                        render={renderFlagDetails}
                    />
                    <Table.Column
                        align="center"
                        className="dev-only"
                        key="isDevOnly"
                        title="Dev Only"
                        render={(active) => renderIsActive(active, 'dev')}
                    />
                    <Table.Column
                        align="right"
                        className="actions"
                        key="actions"
                        title="Actions"
                        render={renderActions}
                    />
                </Table>

                <FlagsEditor
                    key={flagsEditor.drawerKey}
                    flag={flagsEditor.flagToEdit}
                    mode={flagsEditor.flagToEdit?.getKey?.() ? 'edit' : 'create'}
                    onClose={handleCloseFlagDrawer}
                    onSubmit={onFlagUpdated}
                    visible={flagsEditor.showFlagEditor}
                />
            </div>
        )
    }

    return render()
}
